/**
 * @typedef {import('./ContactsData').default} ContactsData
 * @typedef {import('../../App/shared/Query/Parameters').default} FilterQueryParameters
 * @typedef {import('../../App/shared/Query/Parameters').default} QueryParameters
 * @typedef {import('../../Auth/AuthService').default} AuthService
 * @typedef {import('./AgentContact').default} AgentContact
 * @typedef {import('./ContactResponse').ContactCount} ContactCount
 * @typedef {import('./ContactResponse').default} Response
 * @typedef {import('./GetsContacts').default} GetsContacts
 * @typedef {import('@mooveguru/js-http-client').BrowserClient} HttpClient
 */
import { prepareQueryForUrl } from '../../App/shared/Query';
import apiUrls from '../../config/local/api-urls';
import ServiceError from '../../shared/Errors/ServiceError';
import utilities from '@mooveguru/js-utilities';

/**
 * @implements {GetsContacts}
 */
// TODO: See if Service can be consolidated with Admin Contacts Service into one Contacts Component
export default class AgentContactService {
	/** @param {{authService: AuthService, httpClient: HttpClient }} dependencies */
	constructor(dependencies) {
		this.authService = dependencies.authService;
		this.httpClient = dependencies.httpClient;
	}

	/**
	 * @returns {Promise<{accepted: number, percent: number, total: number}>}
	 */
	async getTotalContacts() {
		const response = await this.httpClient.get(
			`${apiUrls.me.contacts}?type=group`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		const total = response.body.total;

		const accepted = response.body.enrolled_contacts;

		return {
			accepted,
			percent:
				total && accepted ? Math.ceil(100 * (accepted / total)) : 0,
			total,
		};
	}

	/**
	 * @param {QueryParameters} [query]  -- Accepts number for legacy purposes
	 * @returns {Promise<ContactsData>}
	 */
	async getContacts(query) {
		if (!query?.limit) {
			query = {
				...query,
				limit: 50,
			};
		}

		const params = prepareQueryForUrl(query, true);

		const response = await this.httpClient.get(
			apiUrls.me.contacts + params,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		return {
			contacts: response.body.contacts.map(
				AgentContactService.#mapContactToResponse
			),
			moverCounts: response.body.mover_counts ?? [],
			totalContacts: response.body.total,
			totalPages: response.body.pages,
		};
	}

	/**
	 * @param {Response} contact
	 * @returns {Omit<AgentContact, 'address'> & { address: string }}
	 */
	// eslint-disable-next-line complexity
	static #mapContactToResponse(contact) {
		return {
			acceptedInvite: contact.accepted_invite,
			address: AgentContactService.formatAddress(contact) || '—',
			agentId: contact.agent_id,
			email: contact?.email_address,
			firstName: contact?.name?.first ?? null,
			group: {
				id: contact.group?.id ?? null,
				isSubscribed: contact.group?.is_subscribed ?? false,
				name: contact.group?.name ?? null,
			},
			id: contact?.id,
			landline: contact?.landline_phone_number ?? null,
			lastLoggedInAt: contact?.last_logged_in_at
				? utilities.date.convertDateToDateString(
						new Date(contact?.last_logged_in_at)
				  )
				: null,
			latestActivityAt: contact?.latest_activity_at
				? new Date(contact?.latest_activity_at).toLocaleDateString(
						'en-US',
						{
							day: '2-digit',
							month: '2-digit',
							year: '2-digit',
						}
				  )
				: null,
			latestActivityDescription:
				contact?.latest_activity_description ?? null,
			leadScore: contact?.lead_score ?? 0,
			leadScores: contact?.lead_scores ?? [],
			mobile: contact?.mobile_phone_number ?? null,
			moveDate: contact?.move_date
				? utilities.date.convertDateToDateString(
						new Date(contact?.move_date)
				  )
				: '—',
			name:
				contact?.name?.first || contact?.name?.last
					? `${contact?.name?.first ?? ''} ${contact?.name?.last ?? ''}`.trim() // prettier-ignore
					: '—',
			tagIds: contact?.tag_ids ?? [],
		};
	}

	/**
	 * @param {string} agentId
	 * @returns {Promise<ContactCount | ContactCount[]>}
	 */
	async getContactsCount(agentId) {
		const endpoint = apiUrls.agents.contactCount.replace(
			':agentId',
			agentId
		);

		const response = await this.httpClient.get(
			endpoint,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		return Array.isArray(response.body)
			? response.body.map(AgentContactService.#mapContactCountResponse)
			: AgentContactService.#mapContactCountResponse(response.body);
	}

	/**
	 * @param {ContactCount & {has_subscription: boolean}} contact
	 * @returns {ContactCount}
	 */
	static #mapContactCountResponse(contact) {
		return {
			count: contact.count ?? 0,
			hasSubscription: contact?.has_subscription ?? false,
			id: contact?.id,
			name: contact?.name,
		};
	}

	/**
	 * @private
	 * @param {string[]} strings
	 * @returns {string}
	 */
	static formatAddressLine(strings) {
		return strings.filter((string) => string !== '').join(', ');
	}

	/**
	 * @private
	 * @param {Response} contact
	 * @returns {string}
	 */
	static formatAddress(contact) {
		if (!contact.address) {
			return 'N/A';
		}

		return AgentContactService.formatAddressLine([
			AgentContactService.formatAddressLine([
				contact.address.street_address_1 ?? '',
				contact.address.street_address_2 ?? '',
			]),
			this.formatAddressLine([`${contact.address.city ?? ''}`.trim()]),
		]);
	}

	/**
	 * Invite contact by id
	 * @param {string} id
	 */
	async inviteContact(id) {
		if (!id) {
			throw new Error('Contact not found');
		}
		try {
			const response = await this.httpClient.post(
				`${apiUrls.contacts.invite}/${id}`,
				new Headers({
					Authorization: `Bearer ${this.authService.getAccessToken()}`,
				})
			);

			if (!response.isOk) {
				throw new ServiceError(
					'Something went wrong while trying to invite your contact.',
					response.body
				);
			}
		} catch (error) {
			throw new Error(
				'Something went wrong while trying to invite your contact.'
			);
		}
	}
}
